import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import AuditSummary from "./AuditSummary";
import AuditProgress from "./AuditProgress";

// Audit tab of the Project Tracker (Owner only).
// Shows the code-audit runs that run-code-audit.ps1 uploads from the laptop (collection "codeAudits"), day by day.
// Read-only: nothing here changes any data. "Export" downloads the findings as one JSON file.
const SEV_ORDER = ["critical", "high", "moderate", "low"];
const SEV_COLOR = { critical: "#ff5c5c", high: "#f87171", moderate: "#fbbf24", low: "#8d93ad" };
const PAGE = 100;

const C = {
  card: { background: "#1a1d2e", border: "1px solid #2a2e45", borderRadius: 10, padding: "12px 14px", marginBottom: 10 },
  btn: { background: "#2a2e45", color: "#e8eaf6", border: "1px solid #3a3f5c", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 },
  btnMain: { background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  select: { background: "#0f111a", border: "1px solid #3a3f5c", borderRadius: 8, padding: "6px 10px", color: "#e8eaf6", fontSize: 13 },
};

const timeOf = (runId) => (runId.slice(11, 13) && runId.slice(13, 15) ? runId.slice(11, 13) + ":" + runId.slice(13, 15) : "");
const chip = (label, n, color) => (
  <span key={label} style={{ background: color + "22", color, borderRadius: 999, padding: "2px 9px", fontSize: 12, marginRight: 6, whiteSpace: "nowrap" }}>{label} {n}</span>
);

export default function TrackerAuditTab() {
  const [runs, setRuns] = useState([]);const [sonar, setSonar] = useState([]);const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [openId, setOpenId] = useState(null);
  const [findings, setFindings] = useState({}); // runId -> array
  const [fProject, setFProject] = useState("all");
  const [fSev, setFSev] = useState("all");
  const [shown, setShown] = useState(PAGE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "codeAudits"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.runId < b.runId ? 1 : -1));
        setRuns(list);try { const ss = await getDocs(collection(db, "sonarScans")); const sl = ss.docs.map((d) => ({ id: d.id, ...d.data() })); sl.sort((a, b) => (a.runId < b.runId ? 1 : -1)); setSonar(sl); } catch (e) { /* Sonar data is optional */ }
      } catch (e) { setMsg("Error loading the audit runs: " + e.message); }
      setLoading(false);
    })();
  }, []);

  const loadFindings = async (runId) => {
    if (findings[runId]) return findings[runId];
    const snap = await getDocs(collection(db, "codeAudits", runId, "findings"));
    const list = snap.docs.map((d) => d.data());
    list.sort((a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity) || String(a.project).localeCompare(b.project));
    setFindings((f) => ({ ...f, [runId]: list }));
    return list;
  };

  const toggle = async (runId) => {
    if (openId === runId) { setOpenId(null); return; }
    setOpenId(runId); setFProject("all"); setFSev("all"); setShown(PAGE);
    try { await loadFindings(runId); } catch (e) { setMsg("Error loading findings: " + e.message); }
  };

  const exportRuns = async (list, label) => {
    setBusy(true);
    try {
      const out = [];
      for (const r of list) {
        const f = await loadFindings(r.id);
        const { id, ...rest } = r;
        out.push({ ...rest, uploadedAt: r.uploadedAt && r.uploadedAt.toDate ? r.uploadedAt.toDate().toISOString() : null, findings: f });
      }
      const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), runs: out }, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "code-audit-export-" + label + ".json";
      a.click();
      URL.revokeObjectURL(url);
      setMsg("Exported " + out.length + " run(s) to code-audit-export-" + label + ".json");
    } catch (e) { setMsg("Export error: " + e.message); }
    setBusy(false);
  };

  // ---- comparison helpers (lower numbers are always better) ----
  const ci = (o, k) => { if (!o) return null; const f = Object.keys(o).find((q) => q.toLowerCase() === String(k).toLowerCase()); return f ? o[f] : null; };
  const num = (v) => (typeof v === "number" ? v : undefined);
  const metricsOf = (x, sp) => {
    x = x || {}; const ck = x.checks || {};
    return {
      npmApp: x.npmApp ? num(x.npmApp.total) : undefined,
      npmFn: x.npmFunctions ? num(x.npmFunctions.total) : undefined, npmAppHigh: x.npmApp && typeof x.npmApp.high === "number" ? (x.npmApp.critical || 0) + x.npmApp.high : undefined, npmFnHigh: x.npmFunctions && typeof x.npmFunctions.high === "number" ? (x.npmFunctions.critical || 0) + x.npmFunctions.high : undefined, npmAppRealTotal: x.npmAppReal ? num(x.npmAppReal.total) : undefined, npmAppRealHigh: x.npmAppReal ? (x.npmAppReal.critical || 0) + (x.npmAppReal.high || 0) : undefined, npmFnRealTotal: x.npmFunctionsReal ? num(x.npmFunctionsReal.total) : undefined, npmFnRealHigh: x.npmFunctionsReal ? (x.npmFunctionsReal.critical || 0) + (x.npmFunctionsReal.high || 0) : undefined,
      semgrep: x.semgrep ? num(x.semgrep.findings) : undefined,
      openRules: num(ck.openRules),
      secrets: ck.sensitiveTracked !== undefined || ck.tokenFiles !== undefined ? (ck.sensitiveTracked || 0) + (ck.tokenFiles || 0) : undefined,
      outdated: num(ck.outdated),
      major: num(ck.majorBehind),
      sonar: sp ? (sp.bugs || 0) + (sp.vulnerabilities || 0) + (sp.codeSmells || 0) + (sp.hotspots || 0) : undefined,
    };
  };
  const compareM = (cur, prev) => {
    let better = 0, worse = 0, same = 0;
    Object.keys(cur).filter((k) => k !== "npmApp" && k !== "npmFn" && k !== "npmAppRealTotal" && k !== "npmFnRealTotal").forEach((k) => {
      if (typeof cur[k] === "number" && prev && typeof prev[k] === "number") {
        const d = cur[k] - prev[k];
        if (d < 0) better++; else if (d > 0) worse++; else same++;
      }
    });
    return { better, worse, same };
  };
  const verdictOf = (c) => (c.better + c.worse + c.same === 0 ? { text: "no earlier data", color: "#7b82a0" }
    : c.better && !c.worse ? { text: "Improved", color: "#4ade80" }
    : c.worse && !c.better ? { text: "Worse", color: "#f87171" }
    : c.better && c.worse ? { text: "Mixed", color: "#fbbf24" }
    : { text: "No change", color: "#8d93ad" });
  const delta = (cur, prev) => {
    if (typeof cur !== "number" || typeof prev !== "number") return null;
    if (cur < prev) return <span style={{ color: "#4ade80", marginLeft: 6, fontSize: 12 }}>{"\u25BC " + (prev - cur)}</span>;
    if (cur > prev) return <span style={{ color: "#f87171", marginLeft: 6, fontSize: 12 }}>{"\u25B2 " + (cur - prev)}</span>;
    return <span style={{ color: "#7b82a0", marginLeft: 6, fontSize: 12 }}>=</span>;
  };

  const days = [];
  runs.forEach((r) => { let d = days.find((x) => x.day === r.day); if (!d) { d = { day: r.day, runs: [] }; days.push(d); } d.runs.push(r); });

  const rows = (findings[openId] || []).filter((x) => (fProject === "all" || x.project === fProject) && (fSev === "all" || x.severity === fSev));
  const projectsOfOpen = openId ? Array.from(new Set((findings[openId] || []).map((x) => x.project))) : [];

  return (
    <div>
      <div style={{ fontSize: 14, color: "#e8eaf6", marginBottom: 10 }}>
        <b>Audit</b> - code audit findings uploaded from your laptop, day by day. Read-only. Only the Owner sees this tab.
      </div>
      {msg && <div style={{ ...C.card, color: "#c8cce0", fontSize: 13 }}>{msg}</div>}
      <div style={{ marginBottom: 12 }}>
        <button style={C.btnMain} disabled={busy || runs.length === 0} onClick={() => exportRuns(runs, "all-days")}>{busy ? "Exporting..." : "Export all days (one file)"}</button>
      </div>
      {loading && <div style={{ color: "#7b82a0", fontSize: 13 }}>Loading...</div>}
      {!loading && runs.length === 0 && <div style={{ color: "#7b82a0", fontSize: 13 }}>No audit runs uploaded yet. They appear here after the next scheduled run.</div>}
      {days.map((d) => (
        <div key={d.day}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0 8px", borderBottom: "1px solid #2a2e45", paddingBottom: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#8b85ff" }}>{d.day}</div>
            <button style={C.btn} disabled={busy} onClick={() => exportRuns(d.runs, d.day)}>Export this day</button>
          </div>
          {d.runs.map((r) => {
            const idx = runs.indexOf(r);
            const prevRun = idx >= 0 ? runs[idx + 1] : undefined;
            const sc0 = sonar.find((s) => s.day === r.day);
            const scPrev = sc0 ? sonar[sonar.indexOf(sc0) + 1] : undefined;
            const names = Object.keys(r.projects || {}).sort();
            const per = names.map((p) => {
              const cur = metricsOf(r.projects[p], sc0 ? ci(sc0.projects, p) : null);
              const pr = prevRun ? ci(prevRun.projects, p) : null;
              const prev = pr ? metricsOf(pr, scPrev ? ci(scPrev.projects, p) : null) : null;
              return { p, cur, prev, pr, v: verdictOf(prev ? compareM(cur, prev) : { better: 0, worse: 0, same: 0 }) };
            });
            const cnt = (t) => per.filter((x) => x.v.text === t).length;
            const imp = cnt("Improved"), wor = cnt("Worse"), mix = cnt("Mixed"), sam = cnt("No change");
            let head = null;
            if (!prevRun) head = { t: "First run - nothing earlier to compare with.", c: "#8d93ad" };
            else if (imp + wor + mix + sam === 0) head = { t: "The previous run has no comparable numbers yet.", c: "#8d93ad" };
            else {
              const h = (imp + mix > 0 && wor + mix === 0) ? ["WORKED ON - numbers went down", "#4ade80"]
                : (wor > 0 && imp + mix === 0) ? ["GOT WORSE - numbers went up", "#f87171"]
                : (imp + wor + mix > 0) ? ["MIXED - some better, some worse", "#fbbf24"]
                : ["NO PROGRESS - nothing changed", "#fbbf24"];
              head = { t: h[0] + " (compared with " + prevRun.day + " " + timeOf(prevRun.runId) + "): " + imp + " improved, " + wor + " worse, " + mix + " mixed, " + sam + " unchanged", c: h[1] };
            }
            return (
            <div key={r.id} style={C.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Code audit {timeOf(r.runId) || r.runId}</div>
                <div>
                  {SEV_ORDER.map((s) => chip(s, (r.totals && r.totals[s]) || 0, SEV_COLOR[s]))}
                  <button style={{ ...C.btn, marginLeft: 6 }} onClick={() => toggle(r.id)}>{openId === r.id ? "Hide" : "View"}</button>
                </div>
              </div>
              {idx === 0 && <AuditSummary run={r} prevRun={prevRun} sonar={sc0} sonarPrev={scPrev} showDetail={showDetail} onToggle={() => setShowDetail((v) => !v)} />}{idx === 0 && showDetail && <AuditProgress per={per} />}<div style={{ marginTop: 8, overflowX: "auto", display: idx === 0 && !showDetail ? "none" : "block" }}>
                <div style={{ fontSize: 12, color: "#8d93ad", marginBottom: 6 }}>
                  Code audit: {r.day} {timeOf(r.runId)}{r.uploadedAt && r.uploadedAt.toDate ? " (uploaded " + r.uploadedAt.toDate().toLocaleString() + ")" : ""}{(() => { const sc = sonar.find((s) => s.day === r.day); return sc ? "   |   Sonar scan: " + sc.day + " " + timeOf(sc.runId) + (sc.uploadedAt && sc.uploadedAt.toDate ? " (uploaded " + sc.uploadedAt.toDate().toLocaleString() + ")" : "") : "   |   Sonar scan: none for this day yet"; })()}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: head.c, marginBottom: 2 }}>{head.t}</div>
                <div style={{ fontSize: 11, color: "#7b82a0", marginBottom: 8 }}>Green {"\u25BC"} = fewer than last run (good). Red {"\u25B2"} = more than last run (bad). Sonar = all four Sonar numbers added together. High = critical plus high. Reach users = warnings that can reach the app people use (build tools such as react-scripts left out, an estimate). Under a project name: red + = new warning since last run, green - = warning gone.</div>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: 13, color: "#c8cce0" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#8d93ad", borderBottom: "1px solid #2a2e45" }}>
                      <th style={{ padding: "6px 8px" }}>Project</th>
                      <th style={{ padding: "6px 8px" }}>Since last run</th>
                      <th style={{ padding: "6px 8px" }}>npm (app)</th>
                      <th style={{ padding: "6px 8px" }}>npm (functions)</th>
                      <th style={{ padding: "6px 8px" }}>Semgrep</th>
                      <th style={{ padding: "6px 8px" }}>Open rules</th>
                      <th style={{ padding: "6px 8px" }}>Secrets</th>
                      <th style={{ padding: "6px 8px" }}>Outdated</th>
                      <th style={{ padding: "6px 8px" }}>Sonar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {per.map(({ p, cur, prev, pr, v }) => {
                      const x = r.projects[p];
                      const pv = prev || {};
                      const sp = sc0 ? ci(sc0.projects, p) : null;
                      const dash = (n) => (typeof n === "number" ? n : "-"); const advOf = (o) => [...(o.advApp || []), ...(o.advFunctions || []).map((s) => s + " [functions]")]; const hasAdv = pr && Array.isArray(x.advApp) && Array.isArray(pr.advApp); const curAdv = hasAdv ? new Set(advOf(x)) : new Set(); const prevAdv = hasAdv ? new Set(advOf(pr)) : new Set(); const nwAdv = [...curAdv].filter((z) => !prevAdv.has(z)); const goneAdv = [...prevAdv].filter((z) => !curAdv.has(z)); const shortAdv = (z) => z.split(" | ").slice(0, 2).join(" ");
                      return (
                        <tr key={p} style={{ borderTop: "1px solid #23273a" }}>
                          <td style={{ padding: "6px 8px", color: "#e8eaf6", fontWeight: 600 }}>{p}{(nwAdv.length > 0 || goneAdv.length > 0) && <div style={{ fontWeight: 400, fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>{nwAdv.slice(0, 6).map((z) => <div key={"n" + z} style={{ color: "#f87171" }} title={z}>{"+ " + shortAdv(z)}</div>)}{nwAdv.length > 6 && <div style={{ color: "#f87171" }}>{"+ " + (nwAdv.length - 6) + " more new"}</div>}{goneAdv.slice(0, 6).map((z) => <div key={"g" + z} style={{ color: "#4ade80" }} title={z}>{"- " + shortAdv(z)}</div>)}{goneAdv.length > 6 && <div style={{ color: "#4ade80" }}>{"- " + (goneAdv.length - 6) + " more gone"}</div>}</div>}</td>
                          <td style={{ padding: "6px 8px", color: v.color, fontWeight: 600 }}>{v.text}</td>
                          <td style={{ padding: "6px 8px" }}>{dash(cur.npmApp)}{delta(cur.npmApp, pv.npmApp)}{typeof cur.npmAppHigh === "number" && <div style={{ fontSize: 12, color: "#8d93ad" }}>{cur.npmAppHigh + " high"}{delta(cur.npmAppHigh, pv.npmAppHigh)}</div>}{typeof cur.npmAppRealTotal === "number" && <div style={{ fontSize: 12, color: "#8d93ad" }}>{"reach users: " + cur.npmAppRealTotal + " (" + cur.npmAppRealHigh + " high)"}{delta(cur.npmAppRealHigh, pv.npmAppRealHigh)}</div>}</td>
                          <td style={{ padding: "6px 8px" }}>{dash(cur.npmFn)}{delta(cur.npmFn, pv.npmFn)}{typeof cur.npmFnHigh === "number" && <div style={{ fontSize: 12, color: "#8d93ad" }}>{cur.npmFnHigh + " high"}{delta(cur.npmFnHigh, pv.npmFnHigh)}</div>}{typeof cur.npmFnRealTotal === "number" && <div style={{ fontSize: 12, color: "#8d93ad" }}>{"reach users: " + cur.npmFnRealTotal + " (" + cur.npmFnRealHigh + " high)"}{delta(cur.npmFnRealHigh, pv.npmFnRealHigh)}</div>}</td>
                          <td style={{ padding: "6px 8px" }}>{x.semgrep ? x.semgrep.findings + (x.semgrep.scanErrors ? " (" + x.semgrep.scanErrors + " not scanned)" : "") : "-"}{delta(cur.semgrep, pv.semgrep)}</td>
                          <td style={{ padding: "6px 8px" }}>{dash(cur.openRules)}{delta(cur.openRules, pv.openRules)}</td>
                          <td style={{ padding: "6px 8px" }}>{dash(cur.secrets)}{delta(cur.secrets, pv.secrets)}</td>
                          <td style={{ padding: "6px 8px" }}>{typeof cur.outdated === "number" ? cur.outdated + " (" + dash(cur.major) + " major)" : "-"}{delta(cur.outdated, pv.outdated)}</td>
                          <td style={{ padding: "6px 8px" }}>{sp ? sp.bugs + " bugs, " + sp.vulnerabilities + " vulnerabilities, " + sp.codeSmells + " code smells, " + sp.hotspots + " hotspots" : <span style={{ color: "#7b82a0" }}>no data yet</span>}{delta(cur.sonar, pv.sonar)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {openId === r.id && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    <select style={C.select} value={fProject} onChange={(e) => { setFProject(e.target.value); setShown(PAGE); }}>
                      <option value="all">All projects</option>
                      {projectsOfOpen.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select style={C.select} value={fSev} onChange={(e) => { setFSev(e.target.value); setShown(PAGE); }}>
                      <option value="all">All severities</option>
                      {SEV_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span style={{ fontSize: 13, color: "#8d93ad", alignSelf: "center" }}>{rows.length} finding(s)</span>
                  </div>
                  {!findings[r.id] && <div style={{ color: "#7b82a0", fontSize: 13 }}>Loading findings...</div>}
                  <div style={{ overflowX: "auto" }}>
                    {rows.slice(0, shown).map((x, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "7px 0", borderTop: "1px solid #23273a", fontSize: 13, minWidth: 560 }}>
                        <div style={{ width: 76, color: SEV_COLOR[x.severity], fontWeight: 600 }}>{x.severity}</div>
                        <div style={{ width: 130, color: "#aab0c8" }}>{x.project}</div>
                        <div style={{ flex: 1, color: "#e8eaf6" }}>
                          {x.title}{x.kind === "npm" ? " (" + x.scope + ")" : ""}
                          <div style={{ color: "#8d93ad", fontSize: 12 }}>{x.kind === "semgrep" ? x.path + ":" + x.line : x.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {rows.length > shown && <button style={{ ...C.btn, marginTop: 8 }} onClick={() => setShown(shown + PAGE)}>Show more</button>}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
